import { getApiClient } from '../axios-config';

/**
 * Timecards API Service
 * Handles all API calls related to timecards
 */
class TimecardsAPI {
  /**
   * Get timecard options
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Timecard options object
   */
  static async getTimecardOptions(token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/timecards/timecardoptions');
    return response.data.data;
  }

  /**
   * Get user time range
   * @param {string} employeeId - Employee ID
   * @param {string} dateBeg - Start date (yyyy/MM/dd format)
   * @param {string} dateEnd - End date (yyyy/MM/dd format)
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Time range object
   */
  static async getUserTimeRange(employeeId, dateBeg, dateEnd, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/timecards/getusertimerange', {
      params: {
        employee_id: employeeId,
        date_beg: dateBeg,
        date_end: dateEnd,
      },
    });
    return response.data.data;
  }

  /**
   * Get timecards for a week
   * @param {Object} params - Query parameters (weekEndingDate, foremanId, locationId, etc.)
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Timecards data object
   */
  static async getTimecardsWeek(params, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/timecards/gettimecardsweek', {
      params,
    });
    return response.data.data;
  }

  /**
   * Get previous day timecard
   * @param {string} employeeId - Employee ID
   * @param {string} selectedDate - Selected date (YYYY-MM-DD)
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Previous day timecard object
   */
  static async getPreviousDayTimecard(employeeId, selectedDate, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      '/api/timecards/getpreviousdaytimecard',
      {
        params: {
          employee_id: employeeId,
          selected_date: selectedDate,
        },
      }
    );
    return response.data.data;
  }

  /**
   * Add timecard item
   * @param {Object} timecardItem - Timecard item object
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Created timecard item object
   */
  static async addTimecardItem(timecardItem, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/timecards/addtimecarditem', {
      timecard_item: [timecardItem], // Backend expects an array
    });
    // Backend returns addedTimecards array, return the first item
    return response.data.data.addedTimecards?.[0] || timecardItem;
  }

  /**
   * Update timecard (single field update)
   * @param {string} itemId - Timecard item ID
   * @param {string} fieldChanged - Field name that changed
   * @param {any} newValue - New value for the field
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated timecard item object
   */
  static async updateTimecard(itemId, fieldChanged, newValue, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/timecards/updatetimecard', {
      itemId,
      fieldChanged,
      newValue,
    });
    return (
      response.data.data.updatedTimecardItem || {
        itemId,
        [fieldChanged]: newValue,
      }
    );
  }

  /**
   * Update all timecards
   * @param {Object} changedItem - Timecard item with all fields to update
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Update result
   */
  static async updateTimecardAll(changedItem, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/timecards/updatetimecardall', {
      changedItem: changedItem,
    });
    return response.data.data;
  }

  /**
   * Delete timecard
   * @param {string} timecardId - Timecard ID to delete
   * @param {string} token - JWT authentication token
   * @returns {Promise<void>}
   */
  static async deleteTimecard(timecardId, token) {
    const apiClient = getApiClient(token);
    await apiClient.delete('/api/timecards/deletetimecard', {
      params: {
        time_id: timecardId,
      },
    });
  }

  /**
   * Add supervision timecard
   * @param {Object} supervisionData - Supervision timecard data
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Created supervision timecard object
   */
  static async addSupervision(supervisionData, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/timecards/addsupervision', {
      newSupervisionItem: supervisionData,
    });
    return response.data.data.addedSupervisionItem || supervisionData;
  }
}

export default TimecardsAPI;
